import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  CheckCircle,
  Edit,
  Send,
  Star,
  Visibility,
} from "@mui/icons-material";
import { API_CONFIG, apiService } from "../../../../config/api";

type OnboardingConsentMode = "optional" | "required";

type ConsentForm = {
  form_id: string;
  study_id: number | string;
  form_version: number | string;
  study_form_version: number | null;
  form_version_name: string | null;
  form_name: string;
  form_type: "default_template" | "custom_pdf";
  form_status: "draft" | "preview" | "active" | "paused" | "archived";
  language: string;
  is_default: boolean;
  is_active: boolean;
  onboarding_consent_mode: OnboardingConsentMode;
  source_pdf_original_filename: string | null;
  published_at: string | null;
  activated_at: string | null;
  created_at: string;
  updated_at: string;
};

type ParticipantConsentRow = {
  participant_id: string;
  anonymized_id: string | null;
  enrolled_at: string | null;
  consent_id: string | null;
  status:
    | "invited"
    | "pending"
    | "completed"
    | "declined"
    | "withdrawn"
    | "expired"
    | "superseded"
    | null;
  consent_form_id: string | null;
  form_version: number | string | null;
  study_form_version: number | null;
  participant_email: string | null;
  invited_at: string | null;
  first_opened_at: string | null;
  submitted_at: string | null;
  completed_at: string | null;
  signed_at: string | null;
  declined_at: string | null;
  last_reminder_sent_at: string | null;
  last_sent_at: string | null;
  typed_signature: string | null;
  signed_pdf_url: string | null;
  has_signature: boolean | null;
  reminders_sent: number | null;
};

type FormsResponse = {
  items: ConsentForm[];
  total: number;
};

type ConsentFormDetail = ConsentForm & {
  configuration_data?: Record<string, any> | null;
  form_content?: {
    sections?: Array<{
      key?: string;
      title?: string;
      bodyHtml?: string;
      html?: string;
      body?: string;
      order?: number;
    }>;
  } | null;
  require_drawn_signature?: boolean | null;
  preview_url?: string | null;
};

type ParticipantsResponse = {
  items: ParticipantConsentRow[];
  total: number;
};

type SendConsentResponse = {
  ok: boolean;
  total: number;
  sentCount: number;
  failedCount: number;
  emailFailedCount: number;
  results: Array<{
    ok: boolean;
    participantId: string;
    consentId?: string | null;
    emailSent?: boolean;
    emailError?: string | null;
    error?: string;
  }>;
};

const API_BASE = API_CONFIG.BASE_URL.replace(/\/$/, "");

function formatDate(value?: string | null) {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString();
}

function versionLabel(form: ConsentForm) {
  const version = form.study_form_version || form.form_version;
  return `Version ${version}`;
}

const compactButtonSx = {
  minWidth: 0,
  px: 1,
  py: 0.25,
  fontSize: "0.72rem",
  lineHeight: 1.4,
  letterSpacing: 0.2,
  "& .MuiButton-startIcon": { mr: 0.5, ml: 0 },
  "& .MuiButton-endIcon": { ml: 0.5, mr: 0 },
};

function getReferenceName(form?: ConsentForm | null) {
  if (!form) return "";
  return form.form_version_name || form.form_name || versionLabel(form);
}

function escapeHtml(input: unknown) {
  if (input === null || input === undefined) return "";
  return String(input)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function generateFormPreviewHtml(form: ConsentFormDetail) {
  const formContent = form.form_content;
  const sections = formContent && Array.isArray(formContent.sections)
    ? formContent.sections
    : [];
  const sectionsHtml = sections
    .slice()
    .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
    .map((section) => `
      <section class="consent-section">
        <h3>${escapeHtml(section.title || section.key || "Consent Section")}</h3>
        <div>${section.bodyHtml || section.html || section.body || ""}</div>
      </section>
    `)
    .join("\n");
  const fallback = form.preview_url
    ? `<p><a href="${escapeHtml(form.preview_url)}">Open the consent preview</a></p>`
    : "<p>No saved preview content is available for this form.</p>";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Preview - ${escapeHtml(getReferenceName(form))}</title>
  <style>
    body { margin:0; padding:16px; font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif; background:#f3f4f6; color:#111827; }
    article { max-width:760px; margin:0 auto; background:#fff; padding:24px; border:1px solid #e5e7eb; border-radius:8px; }
    header { border-bottom:2px solid #990000; margin-bottom:12px; padding-bottom:10px; }
    h1 { margin:0; font-size:20px; color:#990000; }
    .meta { margin-top:4px; font-size:12px; color:#6b7280; }
    .consent-section { padding:12px 0; border-top:1px solid #e5e7eb; }
    .consent-section h3 { margin:0 0 6px; font-size:15px; color:#374151; }
    .consent-section div { font-size:14px; line-height:1.55; color:#374151; }
  </style>
</head>
<body>
  <article>
    <header>
      <h1>${escapeHtml(getReferenceName(form))}</h1>
      <div class="meta">${escapeHtml(versionLabel(form))}</div>
    </header>
    ${sectionsHtml || fallback}
  </article>
</body>
</html>`;
}

async function requestJson<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = await apiService.getAuthToken();
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  const text = await response.text();
  const json = text ? JSON.parse(text) : null;
  if (!response.ok) {
    throw new Error(json?.error || json?.message || `Request failed (${response.status})`);
  }
  return json as T;
}

export default function ConsentManager() {
  const navigate = useNavigate();
  const { studyId: routeStudyId } = useParams<{ studyId: string }>();
  const urlParams = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : "",
  );
  const studyId = routeStudyId || urlParams.get("studyId") || "";

  const [forms, setForms] = React.useState<ConsentForm[]>([]);
  const [selectedFormId, setSelectedFormId] = React.useState("");
  const [participants, setParticipants] = React.useState<ParticipantConsentRow[]>([]);
  const [loadingForms, setLoadingForms] = React.useState(false);
  const [loadingParticipants, setLoadingParticipants] = React.useState(false);
  const [savingDefault, setSavingDefault] = React.useState(false);
  const [savingName, setSavingName] = React.useState(false);
  const [actionPendingId, setActionPendingId] = React.useState<string | null>(null);
  const [pdfPendingId, setPdfPendingId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [versionEditDialogOpen, setVersionEditDialogOpen] = React.useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = React.useState(false);
  const [bulkMode, setBulkMode] = React.useState<"selected" | "missing">("selected");
  const [referenceNameDraft, setReferenceNameDraft] = React.useState("");
  const [selectedParticipantIds, setSelectedParticipantIds] = React.useState<Set<string>>(
    () => new Set(),
  );

  const selectedForm = React.useMemo(
    () => forms.find((form) => form.form_id === selectedFormId) || null,
    [forms, selectedFormId],
  );
  const hasForm = Boolean(selectedForm);

  const completedCount = React.useMemo(
    () => participants.filter((row) => row.status === "completed").length,
    [participants],
  );
  const missingCount = React.useMemo(
    () =>
      participants.filter(
        (row) => !row.consent_id || ["invited", "pending", "expired"].includes(row.status || ""),
      ).length,
    [participants],
  );
  const sendableParticipants = React.useMemo(
    () =>
      participants.filter(
        (row) =>
          row.status !== "completed" &&
          row.status !== "declined" &&
          row.status !== "withdrawn",
      ),
    [participants],
  );
  const selectedSendableParticipants = React.useMemo(
    () =>
      sendableParticipants.filter((row) =>
        selectedParticipantIds.has(row.participant_id),
      ),
    [sendableParticipants, selectedParticipantIds],
  );
  const allSendableSelected =
    sendableParticipants.length > 0 &&
    selectedSendableParticipants.length === sendableParticipants.length;
  const someSendableSelected = selectedSendableParticipants.length > 0;

  const loadForms = React.useCallback(async () => {
    if (!studyId) return;
    setLoadingForms(true);
    setError(null);
    try {
      const params = new URLSearchParams({ studyId });
      const data = await requestJson<FormsResponse>(
        `/api/consent/researcher/forms?${params.toString()}`,
      );
      setForms(data.items || []);
      setSelectedFormId((current) => {
        if (current && data.items?.some((form) => form.form_id === current)) {
          return current;
        }
        const active = data.items?.find((form) => form.is_default);
        return active?.form_id || data.items?.[0]?.form_id || "";
      });
    } catch (err: any) {
      setError(err?.message || "Failed to load consent forms");
      setForms([]);
      setSelectedFormId("");
    } finally {
      setLoadingForms(false);
    }
  }, [studyId]);

  const loadParticipants = React.useCallback(async () => {
    if (!studyId || !selectedFormId) {
      setParticipants([]);
      return;
    }
    setLoadingParticipants(true);
    setError(null);
    try {
      const params = new URLSearchParams({ studyId, formId: selectedFormId });
      const data = await requestJson<ParticipantsResponse>(
        `/api/consent/researcher/form-participants?${params.toString()}`,
      );
      setParticipants(data.items || []);
      setSelectedParticipantIds((current) => {
        const available = new Set((data.items || []).map((row) => row.participant_id));
        const next = new Set<string>();
        current.forEach((id) => {
          if (available.has(id)) next.add(id);
        });
        return next;
      });
    } catch (err: any) {
      setError(err?.message || "Failed to load participants");
      setParticipants([]);
    } finally {
      setLoadingParticipants(false);
    }
  }, [studyId, selectedFormId]);

  React.useEffect(() => {
    loadForms();
  }, [loadForms]);

  React.useEffect(() => {
    loadParticipants();
  }, [loadParticipants]);

  const openReferenceDialog = () => {
    if (!selectedForm) return;
    setReferenceNameDraft(getReferenceName(selectedForm));
    setEditDialogOpen(true);
  };

  const handleSaveReferenceName = async () => {
    if (!studyId || !selectedForm || !referenceNameDraft.trim()) return;
    setSavingName(true);
    setError(null);
    setNotice(null);
    try {
      const result = await requestJson<{ ok: boolean; form: ConsentForm }>(
        `/api/consent/researcher/forms/${encodeURIComponent(selectedForm.form_id)}/reference-name`,
        {
          method: "PATCH",
          body: JSON.stringify({
            studyId,
            referenceName: referenceNameDraft.trim(),
          }),
        },
      );
      setForms((current) =>
        current.map((form) =>
          form.form_id === result.form.form_id ? result.form : form,
        ),
      );
      setNotice("Reference name updated.");
      setEditDialogOpen(false);
    } catch (err: any) {
      setError(err?.message || "Failed to update reference name");
    } finally {
      setSavingName(false);
    }
  };

  const handleMakeDefault = async () => {
    if (!studyId || !selectedForm || selectedForm.is_default) return;
    setSavingDefault(true);
    setError(null);
    setNotice(null);
    try {
      const result = await requestJson<{ ok: boolean; form: ConsentForm }>(
        `/api/consent/researcher/forms/${encodeURIComponent(selectedForm.form_id)}/make-default`,
        {
          method: "POST",
          body: JSON.stringify({ studyId }),
        },
      );
      setForms((current) =>
        current.map((form) =>
          form.form_id === result.form.form_id
            ? result.form
            : { ...form, is_default: false },
        ),
      );
      setNotice(`${versionLabel(result.form)} is now the default consent version.`);
    } catch (err: any) {
      setError(err?.message || "Failed to make this form default");
    } finally {
      setSavingDefault(false);
    }
  };

  const handleCreateNewForm = () => {
    if (!studyId) {
      setError("Study ID is required to create a consent form.");
      return;
    }
    navigate(
      `/researcher/study/${encodeURIComponent(studyId)}/consent-management/create`,
    );
  };

  const handlePreviewForm = async () => {
    if (!studyId || !selectedForm) return;
    setActionPendingId("preview");
    setError(null);
    setNotice(null);
    try {
      const result = await requestJson<{ ok: boolean; form: ConsentFormDetail }>(
        `/api/consent/researcher/forms/${encodeURIComponent(selectedForm.form_id)}?studyId=${encodeURIComponent(studyId)}`,
      );
      const html = generateFormPreviewHtml(result.form);
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const dataUrl = URL.createObjectURL(blob);
      const previewWindow = window.open(dataUrl, "_blank", "noopener,noreferrer");
      if (previewWindow) {
        previewWindow.addEventListener("beforeunload", () => URL.revokeObjectURL(dataUrl));
      }
      setNotice("Preview opened in a new tab.");
    } catch (err: any) {
      setError(err?.message || "Failed to open preview");
    } finally {
      setActionPendingId(null);
    }
  };

  const handleProceedEditVersion = () => {
    if (!studyId || !selectedForm) return;
    setVersionEditDialogOpen(false);
    navigate(
      `/researcher/study/${encodeURIComponent(studyId)}/consent-management/create?sourceFormId=${encodeURIComponent(selectedForm.form_id)}`,
    );
  };

  const sendParticipants = async (
    rows: ParticipantConsentRow[],
    pendingId: string,
  ) => {
    if (!studyId || !selectedFormId || rows.length === 0) return;
    setActionPendingId(pendingId);
    setError(null);
    setNotice(null);
    try {
      const result = await requestJson<SendConsentResponse>(
        `/api/consent/researcher/send`,
        {
          method: "POST",
          body: JSON.stringify({
            studyId,
            formId: selectedFormId,
            participants: rows.map((row) => ({
              participantId: row.participant_id,
              email: row.participant_email || undefined,
            })),
          }),
        },
      );
      const emailErrors = Array.from(
        new Set(
          result.results
            .filter((r) => r.ok && !r.emailSent && r.emailError)
            .map((r) => r.emailError!),
        ),
      );
      const emailWarning =
        result.emailFailedCount > 0
          ? ` ${result.emailFailedCount} email${result.emailFailedCount === 1 ? "" : "s"} could not be sent (${emailErrors.join("; ")}), but tracking link${result.emailFailedCount === 1 ? "" : "s"} were stored.`
          : "";
      const linkFailures = result.results
        .filter((r) => !r.ok && r.error)
        .map((r) => r.error!);
      const failureWarning =
        linkFailures.length > 0
          ? ` ${linkFailures.length} participant${linkFailures.length === 1 ? "" : "s"} failed (${linkFailures.join("; ")}).`
          : "";
      setNotice(
        `${result.sentCount} consent link${result.sentCount === 1 ? "" : "s"} prepared.${emailWarning}${failureWarning}`,
      );
      setSelectedParticipantIds(new Set());
      await loadParticipants();
    } catch (err: any) {
      setError(err?.message || "Failed to send consent link");
    } finally {
      setActionPendingId(null);
    }
  };

  const handleSendFormLink = async (row: ParticipantConsentRow) => {
    if (!studyId || !selectedFormId) return;
    await sendParticipants([row], row.participant_id);
  };

  const toggleParticipantSelection = (participantId: string) => {
    setSelectedParticipantIds((current) => {
      const next = new Set(current);
      if (next.has(participantId)) {
        next.delete(participantId);
      } else {
        next.add(participantId);
      }
      return next;
    });
  };

  const toggleAllSendable = () => {
    setSelectedParticipantIds((current) => {
      const next = new Set(current);
      if (allSendableSelected) {
        sendableParticipants.forEach((row) => next.delete(row.participant_id));
      } else {
        sendableParticipants.forEach((row) => next.add(row.participant_id));
      }
      return next;
    });
  };

  const openBulkDialog = (mode: "selected" | "missing") => {
    setBulkMode(mode);
    setBulkDialogOpen(true);
  };

  const handleConfirmBulkSend = async () => {
    const rows =
      bulkMode === "selected" ? selectedSendableParticipants : sendableParticipants;
    setBulkDialogOpen(false);
    await sendParticipants(rows, "bulk");
  };

  const handleResendSignedForm = async (row: ParticipantConsentRow) => {
    if (!studyId || !row.consent_id) return;
    setActionPendingId(row.participant_id);
    setError(null);
    setNotice(null);
    try {
      const result = await requestJson<{ ok: boolean; emailSent: boolean }>(
        `/api/consent/researcher/resend-form`,
        {
          method: "POST",
          body: JSON.stringify({
            studyId,
            consentId: row.consent_id,
            email: row.participant_email || undefined,
          }),
        },
      );
      setNotice(
        result.emailSent
          ? `Signed document copy sent to ${row.participant_email || row.participant_id}.`
          : `Signed document copy is ready for ${row.participant_id}.`,
      );
    } catch (err: any) {
      setError(err?.message || "Failed to send signed document copy");
    } finally {
      setActionPendingId(null);
    }
  };

  const handleOpenSignedPdf = async (row: ParticipantConsentRow) => {
    if (!studyId || !row.consent_id) return;
    setPdfPendingId(row.participant_id);
    setError(null);
    setNotice(null);
    try {
      const token = await apiService.getAuthToken();
      const url = `${API_BASE}/api/consent/${encodeURIComponent(row.consent_id)}/pdf?studyId=${encodeURIComponent(studyId)}`;
      const response = await fetch(url, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) {
        let errorBody = "";
        try {
          const errJson = await response.json();
          errorBody = errJson?.error || errJson?.message || JSON.stringify(errJson);
        } catch {
          errorBody = await response.text().catch(() => "");
        }
        throw new Error(`PDF download failed (${response.status}): ${errorBody}`);
      }
      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error("Generated PDF is empty");
      }
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `consent-${row.consent_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      setNotice("PDF downloaded successfully.");
    } catch (err: any) {
      setError(err?.message || "Failed to open signed PDF");
    } finally {
      setPdfPendingId(null);
    }
  };

  const emptyMessage = forms.length === 0
    ? "Create a form first. No consent form versions are available for this study yet."
    : "Select a consent version to view participant consent status.";

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={2.5}>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "primary.main" }}>
              Consent Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage consent form versions and review participant consent status.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateNewForm}
            disabled={!studyId}
            data-spotlight-id="consent-create-form"
          >
            Create New Form
          </Button>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
        {notice && (
          <Alert severity="info" onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}

        <Paper sx={{ p: 2.5, border: 1, borderColor: "divider" }}>
          {loadingForms && <LinearProgress sx={{ mb: 2 }} />}
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Consent version"
                data-spotlight-id="consent-version-picker"
                value={selectedFormId}
                disabled={forms.length === 0 || loadingForms}
                onChange={(event) => setSelectedFormId(event.target.value)}
              >
                {forms.map((form) => (
                  <MenuItem key={form.form_id} value={form.form_id}>
                    {versionLabel(form)}
                    {form.is_default ? " - Default" : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Reference assigned name
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedForm ? getReferenceName(selectedForm) : "No form created"}
                  </Typography>
                  {selectedForm && (
                    <Chip
                      label={
                        selectedForm.onboarding_consent_mode === "required"
                          ? "Onboarding required"
                          : "Onboarding optional"
                      }
                      color={
                        selectedForm.onboarding_consent_mode === "required"
                          ? "warning"
                          : "default"
                      }
                      size="small"
                      variant="outlined"
                    />
                  )}
                  <Tooltip title="Edit reference name">
                    <span>
                      <IconButton
                        size="small"
                        onClick={openReferenceDialog}
                        disabled={!hasForm}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack
                direction="row"
                spacing={1}
                justifyContent={{ xs: "flex-start", md: "flex-end" }}
                flexWrap="wrap"
                useFlexGap
              >
                <Button
                  variant={selectedForm?.is_default ? "outlined" : "contained"}
                  startIcon={selectedForm?.is_default ? <CheckCircle /> : <Star />}
                  disabled={!hasForm || selectedForm?.is_default || savingDefault}
                  onClick={handleMakeDefault}
                >
                  {selectedForm?.is_default ? "Default" : "Make Default"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Visibility />}
                  disabled={!hasForm || actionPendingId === "preview"}
                  onClick={handlePreviewForm}
                >
                  {actionPendingId === "preview" ? "Opening..." : "Preview"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  disabled={!hasForm}
                  onClick={() => setVersionEditDialogOpen(true)}
                >
                  Edit Form
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {selectedForm ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
              <Chip label={`Updated ${formatDate(selectedForm.updated_at)}`} size="small" variant="outlined" />
            </Stack>
          ) : (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {emptyMessage}
            </Alert>
          )}
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, border: 1, borderColor: "divider" }}>
              <Typography variant="caption" color="text.secondary">
                Participants
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {hasForm ? participants.length : 0}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, border: 1, borderColor: "divider" }}>
              <Typography variant="caption" color="text.secondary">
                Completed consent
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {hasForm ? completedCount : 0}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, border: 1, borderColor: "divider" }}>
              <Typography variant="caption" color="text.secondary">
                Not provided
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {hasForm ? missingCount : 0}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Paper data-spotlight-id="consent-status-table" sx={{ border: 1, borderColor: "divider", overflow: "hidden" }}>
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Participant Consent Status
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Status is shown for the selected consent version only.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={<Send />}
                disabled={!hasForm || !someSendableSelected || actionPendingId === "bulk"}
                onClick={() => openBulkDialog("selected")}
              >
                Send selected ({selectedSendableParticipants.length})
              </Button>
              <Button
                variant="contained"
                startIcon={<Send />}
                disabled={!hasForm || sendableParticipants.length === 0 || actionPendingId === "bulk"}
                onClick={() => openBulkDialog("missing")}
              >
                Send all missing ({sendableParticipants.length})
              </Button>
            </Stack>
          </Box>
          {loadingParticipants && <LinearProgress />}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center" padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allSendableSelected}
                      indeterminate={someSendableSelected && !allSendableSelected}
                      disabled={!hasForm || sendableParticipants.length === 0}
                      onChange={toggleAllSendable}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Participant</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Consent Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>First Opened</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Signed At</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Sent Count</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>PDF</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!hasForm && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 4, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        {emptyMessage}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {hasForm && participants.length === 0 && !loadingParticipants && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 4, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        No participants found for this study.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {hasForm &&
                  participants.map((row) => {
                    const isPending = actionPendingId === row.participant_id;
                    const isPdfPending = pdfPendingId === row.participant_id;
                    const canOpenPdf = row.status === "completed" && Boolean(row.consent_id);
                    const isSendable = sendableParticipants.some(
                      (sendable) => sendable.participant_id === row.participant_id,
                    );
                    return (
                      <TableRow key={row.participant_id} hover>
                        <TableCell align="center" padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={selectedParticipantIds.has(row.participant_id)}
                            disabled={!isSendable || actionPendingId === "bulk"}
                            onChange={() => toggleParticipantSelection(row.participant_id)}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: "monospace" }}>
                            {row.participant_id}
                          </Typography>
                          {row.participant_email && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              {row.participant_email}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            Enrolled {formatDate(row.enrolled_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <ConsentStatusChip status={row.status} />
                        </TableCell>
                        <TableCell align="center">{formatDate(row.first_opened_at)}</TableCell>
                        <TableCell align="center">{formatDate(row.signed_at || row.completed_at)}</TableCell>
                        <TableCell align="center">
                          <RemindersSentCell
                            count={row.reminders_sent ?? 0}
                            lastSentAt={row.last_sent_at || row.last_reminder_sent_at}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {canOpenPdf ? (
                            <Button
                              size="small"
                              variant="text"
                              sx={compactButtonSx}
                              disabled={isPdfPending}
                              onClick={() => handleOpenSignedPdf(row)}
                            >
                              {isPdfPending ? "Opening..." : "Open"}
                            </Button>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {row.status === "completed" ? (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Send sx={{ fontSize: 14 }} />}
                              disabled={!row.consent_id || isPending}
                              onClick={() => handleResendSignedForm(row)}
                              sx={compactButtonSx}
                            >
                              {isPending ? "Sending..." : "Send signed document copy"}
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<Send sx={{ fontSize: 14 }} />}
                              disabled={isPending}
                              onClick={() => handleSendFormLink(row)}
                              sx={compactButtonSx}
                            >
                              {isPending ? "Sending..." : row.consent_id ? "Resend Link" : "Send Form Link"}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>

      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Reference Name</DialogTitle>
        <DialogContent>
          <TextField
            label="Reference assigned name"
            fullWidth
            value={referenceNameDraft}
            onChange={(event) => setReferenceNameDraft(event.target.value)}
            sx={{ mt: 1 }}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveReferenceName}
            disabled={savingName || !referenceNameDraft.trim()}
          >
            {savingName ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={versionEditDialogOpen}
        onClose={() => setVersionEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Form</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Editing a consent form creates a new version based on the current
            form. The existing version will remain unchanged, and the new
            version will not become the default until you choose Make Default.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleProceedEditVersion}>
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Consent Links</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {bulkMode === "selected"
              ? `Send ${selectedSendableParticipants.length} selected participant${selectedSendableParticipants.length === 1 ? "" : "s"}`
              : `Send all ${sendableParticipants.length} missing participant${sendableParticipants.length === 1 ? "" : "s"}`}{" "}
            the link for {selectedForm ? versionLabel(selectedForm) : "the selected consent version"}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<Send />}
            onClick={handleConfirmBulkSend}
            disabled={
              bulkMode === "selected"
                ? selectedSendableParticipants.length === 0
                : sendableParticipants.length === 0
            }
          >
            Send Links
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

function ConsentStatusChip({ status }: { status: ParticipantConsentRow["status"] }) {
  if (!status) {
    return <Chip label="Not provided" color="default" size="small" variant="outlined" />;
  }

  const colorByStatus: Record<
    NonNullable<ParticipantConsentRow["status"]>,
    "default" | "primary" | "success" | "warning" | "error" | "info"
  > = {
    invited: "info",
    pending: "warning",
    completed: "success",
    declined: "error",
    withdrawn: "default",
    expired: "default",
    superseded: "default",
  };

  const label = status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

  return <Chip label={label} color={colorByStatus[status]} size="small" />;
}

function RemindersSentCell({
  count,
  lastSentAt,
}: {
  count: number;
  lastSentAt: string | null;
}) {
  if (!count) {
    return (
      <Typography variant="body2" color="text.secondary">
        0
      </Typography>
    );
  }

  const tooltip = lastSentAt
    ? `Last reminder: ${formatDate(lastSentAt)}`
    : `${count} reminder${count === 1 ? "" : "s"} sent`;

  return (
    <Tooltip title={tooltip} arrow>
      <Box>
        <Chip
          label={String(count)}
          color={count >= 3 ? "warning" : "default"}
          size="small"
          variant={count >= 3 ? "filled" : "outlined"}
        />
        {lastSentAt && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            {formatDate(lastSentAt)}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}
